import React, { useState, useEffect, useRef, useCallback } from 'react';

let cachedCities = null;

async function loadCities() {
  if (cachedCities) return cachedCities;
  const res = await fetch('/data/us-cities.json');
  cachedCities = await res.json();
  return cachedCities;
}

export default function CityAutocomplete({ city, state, onCityChange, onStateChange, labelClassName = 'field-label', required = true }) {
  const [query, setQuery] = useState(city || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    loadCities().then(setCities);
  }, []);

  // keep query in sync if parent resets city
  useEffect(() => {
    setQuery(city || '');
  }, [city]);

  const search = useCallback((val) => {
    if (!val || val.length < 2) { setSuggestions([]); setOpen(false); return; }
    const lower = val.toLowerCase();
    const results = cities
      .filter(c => c.city.toLowerCase().startsWith(lower))
      .slice(0, 8);
    setSuggestions(results);
    setOpen(results.length > 0);
  }, [cities]);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    onCityChange(val);
    search(val);
  };

  const handleSelect = (item) => {
    setQuery(item.city);
    onCityChange(item.city);
    onStateChange(item.state);
    setSuggestions([]);
    setOpen(false);
  };

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div style={{ display: 'flex', gap: '12px', position: 'relative' }} ref={wrapperRef}>
      <label className={labelClassName} style={{ flex: 1, position: 'relative' }}>
        City {required && <span className="required">*</span>}
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => query.length >= 2 && setOpen(suggestions.length > 0)}
          placeholder="City"
          autoComplete="off"
        />
        {open && (
          <ul style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#1a1a1a',
            border: '1px solid rgba(216,161,52,0.4)',
            borderRadius: '8px',
            margin: '4px 0 0',
            padding: '4px 0',
            listStyle: 'none',
            zIndex: 1000,
            maxHeight: '220px',
            overflowY: 'auto'
          }}>
            {suggestions.map((item, i) => (
              <li
                key={i}
                onMouseDown={() => handleSelect(item)}
                style={{
                  padding: '8px 14px',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: '14px',
                  borderBottom: '1px solid rgba(255,255,255,0.05)'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(216,161,52,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {item.city}, <span style={{ color: 'rgba(216,161,52,0.8)', fontSize: '12px' }}>{item.state}</span>
              </li>
            ))}
          </ul>
        )}
      </label>
      <label className={labelClassName} style={{ flex: 1 }}>
        State {required && <span className="required">*</span>}
        <input
          value={state}
          onChange={(e) => onStateChange(e.target.value.toUpperCase().slice(0, 2))}
          placeholder="State"
          maxLength={2}
        />
      </label>
    </div>
  );
}
